import type { WorkflowFilters, WorkflowsPageParams } from "./workflowsApi";

/**
 * Query key factory for workflow data. All keys live under `["workflows"]`
 * so a blanket `invalidate(workflowsKeys.all)` flushes lists, status counts,
 * owner counts, and details together — used after CRUD mutations (see
 * `invalidateWorkflowListCache` in workflowsStore).
 *
 * For finer-grained invalidation, pass a prefix key directly:
 *   qc.invalidateQueries({ queryKey: ["workflows", "list"] })   — all list pages
 *   qc.invalidateQueries({ queryKey: ["workflows", "counts"] }) — all counts variants
 *   qc.invalidateQueries({ queryKey: ["workflows", "detail"] }) — all detail rows
 */
export const workflowsKeys = {
  all: ["workflows"] as const,
  list: (params: WorkflowsPageParams) => ["workflows", "list", params] as const,
  counts: (filters: WorkflowFilters) => ["workflows", "counts", filters] as const,
  ownerCounts: () => ["workflows", "owner-counts"] as const,
  detail: (id: string) => ["workflows", "detail", id] as const,
  // Distinct from `detail` — the name-lookup path uses `fetchWorkflowByIdOrNull`
  // (returns null on 404 to avoid console spam) while `detail` uses
  // `fetchWorkflowById` (throws on 404 for page-level error boundaries). Sharing
  // a key would let `null` leak back into `useWorkflow` consumers as a success
  // state instead of the expected error state.
  nameLookup: (id: string) => ["workflows", "name-lookup", id] as const,
} as const;
